import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2, Loader2, Smartphone, RefreshCw, FilterX, X } from "lucide-react";
import { api, faDigits, faNum, fmtDate } from "../lib/api";
import { Pagination } from "./Inquiries";

const PAGE_SIZE = 20;
const EMPTY = { national_code: "", mobile: "", status: "" };

const STATUS = {
  matched: { label: "تطابق", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  not_matched: { label: "عدم تطابق", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const inputCls =
  "w-full min-w-[120px] px-2 py-1.5 rounded-lg border border-slate-300 focus:border-teal-500 outline-none text-xs bg-white";

export default function MobileCache() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      Object.entries(applied).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/mobile-cache", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("خطا در دریافت کش شماره موبایل‌ها");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, applied]);

  // فیلتر با تأخیر کوتاه
  useEffect(() => {
    const t = setTimeout(() => { setApplied(filters); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const hasFilters = Object.values(filters).some((v) => v);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const key = (r) => `${r.national_code}|${r.mobile}`;

  const recheck = async (r) => {
    setBusy((b) => ({ ...b, [key(r)]: true }));
    try {
      const { data } = await api.post(`/admin/mobile-cache/${r.national_code}/${r.mobile}/recheck`);
      const d = data.data;
      if (!d.changed) {
        toast.success("استعلام انجام شد؛ وضعیت تغییری نکرد");
      } else {
        toast.success(`وضعیت به‌روز شد: ${STATUS[d.old_status]?.label || "—"} ← ${STATUS[d.status]?.label || d.status}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "استعلام انجام نشد");
    }
    setBusy((b) => ({ ...b, [key(r)]: false }));
  };

  const remove = async (r) => {
    if (!window.confirm(`حذف ${faDigits(r.mobile)} برای کدملی ${faDigits(r.national_code)}؟`)) return;
    try {
      await api.delete(`/admin/mobile-cache/${r.national_code}/${r.mobile}`);
      toast.success("حذف شد");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطا در حذف");
    }
  };

  return (
    <div data-testid="mobile-cache-page" className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Smartphone className="text-teal-600" /> کش شماره موبایل‌ها
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          هر ترکیب کدملی و شماره موبایل یک بار ذخیره می‌شود — مجموع <b className="tabular-nums">{faNum(total)}</b> رکورد
        </p>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 text-xs text-slate-500">
          <span>برای هر ستون فیلتر بگذارید. کد ملی و موبایل با پیشوند جستجو می‌شوند.</span>
          {hasFilters && (
            <button onClick={() => setFilters(EMPTY)}
              className="inline-flex items-center gap-1 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded-lg">
              <FilterX size={14} /> پاک کردن فیلترها
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-600 font-medium">
              <tr>
                <th className="py-3 px-4 border-b whitespace-nowrap">شماره موبایل</th>
                <th className="py-3 px-4 border-b whitespace-nowrap">کد ملی</th>
                <th className="py-3 px-4 border-b whitespace-nowrap">وضعیت تطابق</th>
                <th className="py-3 px-4 border-b whitespace-nowrap">تاریخ و ساعت آخرین بروزرسانی</th>
                <th className="py-3 px-4 border-b whitespace-nowrap">عملیات</th>
              </tr>
              <tr>
                <th className="py-2 px-3 border-b font-normal">
                  <FilterInput value={filters.mobile} onChange={(v) => setFilter("mobile", v)} placeholder="مثلا 0912" />
                </th>
                <th className="py-2 px-3 border-b font-normal">
                  <FilterInput value={filters.national_code} onChange={(v) => setFilter("national_code", v)} placeholder="مثلا 157" />
                </th>
                <th className="py-2 px-3 border-b font-normal">
                  <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className={inputCls}>
                    <option value="">همه</option>
                    <option value="matched">تطابق</option>
                    <option value="not_matched">عدم تطابق</option>
                  </select>
                </th>
                <th className="py-2 px-3 border-b" />
                <th className="py-2 px-3 border-b" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-8 text-center"><Loader2 className="animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-slate-400">موردی یافت نشد</td></tr>
              ) : (
                items.map((r, i) => {
                  const st = STATUS[r.status] || { label: "نامشخص", cls: "bg-slate-100 text-slate-700 border-slate-200" };
                  return (
                    <motion.tr key={key(r)} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.015 }} className="border-b border-slate-100 last:border-0">
                      <td className="py-3 px-4 tabular-nums font-mono whitespace-nowrap">{faDigits(r.mobile)}</td>
                      <td className="py-3 px-4 tabular-nums whitespace-nowrap">{faDigits(r.national_code)}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="py-3 px-4 text-xs whitespace-nowrap tabular-nums">{fmtDate(r.updated_at)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => recheck(r)} disabled={busy[key(r)]}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-medium disabled:opacity-60 whitespace-nowrap">
                            {busy[key(r)] ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                            استعلام مجدد
                          </button>
                          <button onClick={() => remove(r)} title="حذف"
                            className="p-2 rounded-lg hover:bg-rose-50 text-rose-500">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPage={setPage} testidPrefix="mobile-cache" />
      </div>
    </div>
  );
}

function FilterInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <input type="text" inputMode="numeric" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className={`${inputCls} pl-6`} />
      {value && (
        <button type="button" onClick={() => onChange("")}
          className="absolute left-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" title="پاک کردن">
          <X size={12} />
        </button>
      )}
    </div>
  );
}
